import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getCrawl,
  type CrawlDetailResponse,
  type CrawlEdge,
  type CrawlResult,
  type FlowGraph,
  type Journey,
  type PageCategory,
  type PageLabel,
  type PageSnapshot,
  type UiElement,
} from "../lib/api";
import { CrawlTabs } from "../components/CrawlTabs";
import { FlowGraphSvg } from "../components/FlowGraphSvg";
import { HealCompareDialog } from "../components/HealCompareDialog";
import { useAuth } from "../lib/auth";

/**
 * Crawl Map — single-crawl detail. Lifted from UAIP-Mockups.html §crawl-map
 * but simplified for v0: no SVG force graph yet, instead a two-pane layout
 * (page list left, element table right) plus a compact edge list for the
 * BFS tree structure.
 */
export function CrawlMapPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<CrawlDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [healCompareOpen, setHealCompareOpen] = useState(false);
  const { entitlements } = useAuth();
  // Heal generation is Pro+; the catalog field is `heal` (off|beta|on).
  // Free + Starter see the button hidden so the upgrade path is clear;
  // server still 402s defensively (`rejectHealDisabled` in heals/routes).
  const healAllowed =
    entitlements?.allowSemantic === true ||
    entitlements?.plan === "pro" ||
    entitlements?.plan === "team" ||
    entitlements?.plan === "enterprise" ||
    entitlements?.plan === "admin";

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getCrawl(id)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setSelectedUrl(res.crawl.pages[0]?.url ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const selectedPage = useMemo<PageSnapshot | null>(() => {
    if (!data || !selectedUrl) return null;
    return data.crawl.pages.find((p) => p.url === selectedUrl) ?? null;
  }, [data, selectedUrl]);

  if (error) return <ErrorPane message={error} />;
  if (!data || !id) return <LoadingPane />;

  const { summary, crawl } = data;

  return (
    <div>
      <header className="mb-6">
        <div className="flex items-center gap-3 text-xs text-slate-500 font-mono">
          <Link to="/" className="hover:text-slate-300">
            dashboard
          </Link>
          <span>/</span>
          <span className="text-slate-300">{summary.id}</span>
        </div>
        <h1 className="text-2xl font-semibold mt-2 truncate">
          {summary.targetUrl}
        </h1>
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-400">
          <Pill label="pages" value={String(summary.pageCount)} />
          <Pill label="elements" value={String(summary.elementCount)} />
          <Pill
            label="viewport"
            value={`${crawl.target.viewport.width}×${crawl.target.viewport.height}`}
          />
          <Pill label="framework" value={summary.framework} />
          {healAllowed && (
            <button
              type="button"
              onClick={() => setHealCompareOpen(true)}
              className="ml-auto rounded-md border border-indigo-500/60 bg-indigo-600/20 px-2.5 py-0.5 text-xs text-indigo-200 hover:bg-indigo-600/40"
              title="Diff this crawl against an earlier crawl on the same host and propose selector heals"
            >
              Compare with previous crawl
            </button>
          )}
          <Link
            to={`/crawls/${encodeURIComponent(id)}/tests`}
            className={`${healAllowed ? "" : "ml-auto "}text-indigo-400 hover:text-indigo-300`}
          >
            view generated tests →
          </Link>
        </div>
      </header>
      {healCompareOpen && (
        <HealCompareDialog
          currentOutDir={summary.id}
          currentTargetUrl={summary.targetUrl}
          onClose={() => setHealCompareOpen(false)}
        />
      )}

      <CrawlTabs crawlId={id} active="map" />

      {crawl.flowGraph && crawl.flowGraph.nodes.length > 0 ? (
        <div className="mb-6 grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8">
            <FlowGraphView
              graph={crawl.flowGraph}
              selectedUrl={selectedUrl}
              onSelect={setSelectedUrl}
            />
          </div>
          <div className="col-span-12 lg:col-span-4">
            <JourneyPanel journeys={crawl.journeys ?? []} />
          </div>
        </div>
      ) : (
        <FlowGraphEmptyState crawl={crawl} />
      )}

      <div className="grid grid-cols-12 gap-6">
        <aside className="col-span-12 lg:col-span-4 xl:col-span-3">
          <PageList
            pages={crawl.pages}
            pageLabels={crawl.pageLabels ?? {}}
            selectedUrl={selectedUrl}
            onSelect={setSelectedUrl}
          />
          <div className="mt-6">
            <EdgesList edges={crawl.edges ?? []} />
          </div>
        </aside>

        <section className="col-span-12 lg:col-span-8 xl:col-span-9">
          {selectedPage ? (
            <ElementTable page={selectedPage} />
          ) : (
            <div className="text-sm text-slate-500">No page selected.</div>
          )}
        </section>
      </div>
    </div>
  );
}

function PageList({
  pages,
  pageLabels,
  selectedUrl,
  onSelect,
}: {
  pages: PageSnapshot[];
  pageLabels: Record<string, PageLabel[]>;
  selectedUrl: string | null;
  onSelect: (url: string) => void;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50">
      <div className="px-4 py-3 border-b border-slate-800 text-xs uppercase tracking-wide text-slate-400">
        Pages ({pages.length})
      </div>
      <ul className="divide-y divide-slate-800 max-h-[60vh] overflow-auto">
        {pages.map((p, idx) => {
          const active = p.url === selectedUrl;
          const labels = pageLabels[p.url] ?? [];
          // Crawler can capture the same URL multiple times (SPA redirects,
          // multi-entry navigation paths, retried form submits). Bare
          // `key={p.url}` collides with a React duplicate-key warning that
          // can also drop the second occurrence on re-render. Composite
          // `idx::url` keeps each row uniquely identifiable while still
          // being stable across renders (idx is positional, not random).
          return (
            <li key={`${idx}::${p.url}`}>
              <button
                onClick={() => onSelect(p.url)}
                className={`w-full text-left px-4 py-3 hover:bg-slate-800/60 transition ${
                  active ? "bg-slate-800/70" : ""
                }`}
              >
                <div className="text-sm text-slate-200 truncate">{p.title || "(untitled)"}</div>
                <div className="font-mono text-[11px] text-slate-500 truncate mt-0.5">
                  {p.url}
                </div>
                {labels.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {labels.slice(0, 3).map((l) => (
                      <LabelBadge key={l.ruleId} label={l} />
                    ))}
                    {labels.length > 3 && (
                      <span className="text-[10px] text-slate-500 self-center">
                        +{labels.length - 3}
                      </span>
                    )}
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">
                  {p.elements.length} elements
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function LabelBadge({ label }: { label: PageLabel }): JSX.Element {
  const color = colorForCategory(label.category);
  const pct = Math.round(label.confidence * 100);
  return (
    <span
      title={`${label.ruleId} · ${pct}% confidence`}
      className={`inline-flex items-center gap-1 text-[10px] border rounded px-1.5 py-0.5 ${color}`}
    >
      <span className="font-medium">{label.label}</span>
      <span className="opacity-60 font-mono">{pct}</span>
    </span>
  );
}

function colorForCategory(cat: PageCategory): string {
  switch (cat) {
    case "auth":
      return "bg-indigo-500/15 text-indigo-300 border-indigo-500/30";
    case "commerce":
      return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
    case "content":
      return "bg-sky-500/15 text-sky-300 border-sky-500/30";
    case "account":
      return "bg-amber-500/15 text-amber-300 border-amber-500/30";
    case "shell":
      return "bg-slate-600/20 text-slate-400 border-slate-600/30";
    case "errors":
      return "bg-rose-500/15 text-rose-300 border-rose-500/30";
  }
}

function EdgesList({ edges }: { edges: CrawlEdge[] }): JSX.Element {
  const counts = edges.reduce<Record<string, number>>((acc, e) => {
    acc[e.status] = (acc[e.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50">
      <div className="px-4 py-3 border-b border-slate-800 text-xs uppercase tracking-wide text-slate-400 flex items-center justify-between">
        <span>Edges</span>
        <span className="font-mono text-[10px] text-slate-500 normal-case">
          queued {counts.queued ?? 0} · dup {counts.duplicate ?? 0} · oos{" "}
          {counts["out-of-scope"] ?? 0}
        </span>
      </div>
      <ul className="divide-y divide-slate-800 max-h-64 overflow-auto">
        {edges.length === 0 && (
          <li className="px-4 py-3 text-xs text-slate-500">No edges recorded.</li>
        )}
        {edges.map((e, i) => (
          <li key={i} className="px-4 py-2 font-mono text-[11px] flex items-center gap-2">
            <StatusDot status={e.status} />
            <span className="truncate text-slate-400">{e.toUrl}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusDot({
  status,
}: {
  status: CrawlEdge["status"];
}): JSX.Element {
  const color =
    status === "queued"
      ? "bg-emerald-400"
      : status === "duplicate"
      ? "bg-amber-400"
      : "bg-slate-500";
  return <span className={`inline-block h-2 w-2 rounded-full ${color}`} />;
}

function ElementTable({ page }: { page: PageSnapshot }): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50">
      <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-400">
            Elements on page
          </div>
          <div className="text-sm text-slate-200 truncate max-w-xl">
            {page.title || "(untitled)"}
          </div>
        </div>
        <div className="font-mono text-[11px] text-slate-500">
          {page.elements.length} total
        </div>
      </div>
      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-900 text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-800">
            <tr>
              <th className="text-left px-4 py-2 font-medium">uaipId</th>
              <th className="text-left px-4 py-2 font-medium">tag</th>
              <th className="text-left px-4 py-2 font-medium">role</th>
              <th className="text-left px-4 py-2 font-medium">name</th>
              <th className="text-left px-4 py-2 font-medium">strategy</th>
            </tr>
          </thead>
          <tbody>
            {page.elements.map((el) => (
              <ElementRow key={el.uaipId} el={el} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ElementRow({ el }: { el: UiElement }): JSX.Element {
  const strategy = classifyStrategy(el);
  return (
    <tr className="border-b border-slate-800/50 hover:bg-slate-800/30">
      <td className="px-4 py-2 font-mono text-[11px] text-slate-300">{el.uaipId}</td>
      <td className="px-4 py-2 font-mono text-[11px] text-slate-400">{el.tag}</td>
      <td className="px-4 py-2 text-xs text-slate-400">{el.role ?? "—"}</td>
      <td className="px-4 py-2 text-xs text-slate-300 truncate max-w-xs">
        {el.accessibleName ?? "—"}
      </td>
      <td className="px-4 py-2">
        <StrategyBadge strategy={strategy} />
      </td>
    </tr>
  );
}

function StrategyBadge({ strategy }: { strategy: string }): JSX.Element {
  const color =
    strategy === "testid"
      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
      : strategy === "role"
      ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/30"
      : strategy === "id"
      ? "bg-sky-500/15 text-sky-300 border-sky-500/30"
      : strategy === "css"
      ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
      : "bg-slate-600/20 text-slate-400 border-slate-600/30";
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 ${color}`}
    >
      {strategy}
    </span>
  );
}

function classifyStrategy(el: UiElement): string {
  const data = el.attrs.dataAttributes ?? {};
  if (
    data["data-test"] ||
    data["data-testid"] ||
    data["data-qa"] ||
    data["data-cy"]
  )
    return "testid";
  if (el.attrs.id) return "id";
  if (el.role && el.accessibleName) return "role";
  if (el.attrs.className) return "css";
  return "xpath";
}

function Pill({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1 rounded border border-slate-800 bg-slate-900/60 px-2 py-1">
      <span className="text-[10px] uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <span className="font-mono text-slate-200 text-[11px]">{value}</span>
    </span>
  );
}

// ───────────────────────────── Flow graph ─────────────────────────────

/**
 * Hand-rolled SVG flow graph. No d3 dependency — the component is small
 * (≤25 nodes in practice) and a proper force layout would be overkill.
 *
 * Layout:
 *   - BFS from any node with zero incoming edges (the "entry points")
 *     assigns each node a depth/layer. Unreached nodes go to layer 0.
 *   - Nodes in the same layer stack vertically, spaced evenly.
 *   - Edges drawn as quadratic beziers with an arrowhead marker.
 *
 * Selection:
 *   - Clicking a node bubbles the URL up to `onSelect`, keeping the graph
 *     in sync with the PageList / ElementTable selection state.
 */
function FlowGraphView({
  graph,
  selectedUrl,
  onSelect,
}: {
  graph: FlowGraph;
  selectedUrl: string | null;
  onSelect: (url: string) => void;
}): JSX.Element {
  if (graph.nodes.length === 0) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-6 text-sm text-slate-500">
        No labelled pages to graph yet. Re-run the crawl with a URL the rule
        registry recognises.
      </div>
    );
  }
  // SVG layout + per-node rendering moved into apps/web/src/components/
  // FlowGraphSvg.tsx so /coverage can reuse it (Plan Journey 5 overlay).
  // Behavior here is byte-identical: no colorByUrl prop = legacy
  // category-coloured palette.
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50">
      <div className="px-4 py-3 border-b border-slate-800 text-xs uppercase tracking-wide text-slate-400 flex items-center justify-between">
        <span>Flow Graph</span>
        <span className="font-mono text-[10px] text-slate-500 normal-case">
          {graph.nodes.length} nodes · {graph.edges.length} edges
        </span>
      </div>
      <div className="overflow-auto p-2">
        <FlowGraphSvg graph={graph} selectedUrl={selectedUrl} onSelect={onSelect} />
      </div>
    </div>
  );
}

/**
 * Explicit empty-state for the flow-graph panel. Renders when the graph
 * is missing or has zero nodes — previously the whole section silently
 * disappeared, which made the Crawl Map look broken on any site that
 * didn't match our built-in PageRule registry. Tells the user *why*
 * there's no graph and what the actionable levers are.
 */
function FlowGraphEmptyState(props: {
  crawl: CrawlResult;
}): JSX.Element {
  const { crawl } = props;
  const edgeCount = crawl.edges?.length ?? 0;
  const visitedEdges =
    crawl.edges?.filter((e) => e.status === "visited").length ?? 0;
  const labelledPages = crawl.pageLabels
    ? Object.keys(crawl.pageLabels).length
    : 0;

  let reason: string;
  if (labelledPages === 0 && visitedEdges === 0) {
    reason =
      "no PageRule fired on any crawled URL, and no navigation edges were recorded as visited";
  } else if (labelledPages === 0) {
    reason =
      "no PageRule fired — the built-in registry (login / cart / checkout / search / 404) didn't match this site's vocabulary";
  } else if (visitedEdges === 0) {
    reason =
      "no edge reached `status: visited` — pages were crawled but no parent→child navigation was captured";
  } else {
    reason =
      "flow-graph filter elided every node — likely a single-page crawl, or all labels were shell-category only";
  }

  return (
    <div className="mb-6 rounded-xl border border-slate-800 bg-slate-900/50 px-5 py-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 h-2 w-2 rounded-full bg-slate-600" />
        <div className="flex-1">
          <div className="text-sm font-medium text-slate-200">
            No journey graph
          </div>
          <p className="mt-1 text-xs text-slate-400">
            <code className="text-slate-300">@uaip/flow-graph</code> ran but
            produced zero nodes — {reason}.
          </p>
          <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
            <Stat label="pages crawled" value={String(crawl.pages.length)} />
            <Stat label="edges recorded" value={`${visitedEdges}/${edgeCount} visited`} />
            <Stat label="labelled pages" value={String(labelledPages)} />
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Tip: re-run on a site that hits known rule categories
            (<code>saucedemo.com</code>, <code>demoblaze.com</code>) to see
            the SVG render, or extend <code>packages/page-rules/</code> with
            rules that match this site.
          </p>
        </div>
      </div>
    </div>
  );
}

function Stat(props: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">
        {props.label}
      </div>
      <div className="mt-0.5 font-mono text-sm text-slate-200">
        {props.value}
      </div>
    </div>
  );
}

function JourneyPanel({ journeys }: { journeys: Journey[] }): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50">
      <div className="px-4 py-3 border-b border-slate-800 text-xs uppercase tracking-wide text-slate-400 flex items-center justify-between">
        <span>Journeys</span>
        <span className="font-mono text-[10px] text-slate-500 normal-case">
          top {journeys.length}
        </span>
      </div>
      {journeys.length === 0 ? (
        <div className="px-4 py-4 text-xs text-slate-500">
          No journeys met the scoring threshold. This usually means the crawler
          only saw one labelled page, or the links between pages weren&apos;t
          real <code className="font-mono">&lt;a href&gt;</code>s.
        </div>
      ) : (
        <ul className="divide-y divide-slate-800 max-h-[60vh] overflow-auto">
          {journeys.map((j) => (
            <li key={j.name} className="px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[12px] text-slate-200 truncate">
                  {j.name}
                </span>
                <span className="font-mono text-[10px] text-indigo-300">
                  score {j.score.toFixed(1)}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-slate-400 truncate">
                {j.labels.join(" → ")}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ───────────────────────────── Loading / error ─────────────────────────────

function LoadingPane(): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-sm text-slate-400">
      Loading crawl…
    </div>
  );
}

function ErrorPane({ message }: { message: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-200">
      <div className="font-medium">Failed to load crawl</div>
      <div className="font-mono text-xs mt-1 text-rose-300/80">{message}</div>
    </div>
  );
}
